import { Text } from "@/components/ui/Text";
import { ProxyRegistrationError } from "@/lib/proxyRegisterUpstreamClient";
import { Alert } from "@/components/ui/Alert";
import { Stack } from "@/components/ui/Stack";
import { useEffect, useRef } from "react";

type ErrorContent = {
  title: string;
  message: string;
};

function capitalizeFirstCharacter(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

function errorContent(error: unknown): ErrorContent | null {
  if (!error) {
    return null;
  }

  if (error instanceof ProxyRegistrationError) {
    return {
      title: error.title,
      message:
        error.message === error.title
          ? "No additional error details were provided."
          : error.message,
    };
  }

  return {
    title: "Failed to attach identity provider",
    message:
      error instanceof Error && error.message
        ? error.message
        : "An unexpected error occurred. Please try again.",
  };
}

export function IdentityProviderAttachmentErrorAlert({
  error,
}: {
  error: unknown;
}): JSX.Element | null {
  const alertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!error) {
      return;
    }

    alertRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [error]);

  const content = errorContent(error);
  if (!content) {
    return null;
  }

  return (
    <div ref={alertRef}>
      <Alert variant="error" dismissible={false}>
        <Stack gap={1}>
          <Text className="font-medium">{content.title}</Text>
          <Text small>{capitalizeFirstCharacter(content.message)}</Text>
        </Stack>
      </Alert>
    </div>
  );
}
